import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';

import {
  PageProductSearchResponse,
  PageProductShopsPageResponse,
  ProductCollectorResponse,
  ProductSearchResponse,
  ProductSheetResponse,
  ProductShopsPageResponse,
  ProductVariantResponse,
  SearchTextExtension,
} from '@app/api/marketplace/models';
import {
  ProductSearchService,
  ProductsService,
} from '@app/api/marketplace/services';
import { AuthenticationSelectors } from '@authentication/store/authentication.slice';
import { PaymentMethodType } from '@shared/enums/payment-method-type';

import { PageResponse } from './../../shared/interfaces/page-response.interface';
import { ProductSearchActions, ProductSearchSelectors } from './search.slice';

@Injectable({ providedIn: 'root' })
export class ProductSearchEffects {
  searchProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductSearchActions.searchProducts.trigger),
      withLatestFrom(
        this.store.select(ProductSearchSelectors.selectSearchValue)
      ),
      switchMap(([_action, searchValue]) =>
        this.productSearchService
          .searchProduct({
            body: searchValue,
            page: searchValue.page,
            size: searchValue.size,
            sort: searchValue.sort,
            marketplaceSection:
              this.router.url.includes('buying') &&
              !this.router.url.includes('sortiment')
                ? 'BUYING'
                : 'SELLING',
          })
          .pipe(
            map((response: PageProductSearchResponse) => {
              return ProductSearchActions.searchProducts.success({
                response: response as PageResponse<ProductSearchResponse>,
              });
            }),
            catchError(() => of(ProductSearchActions.searchProducts.failure()))
          )
      )
    )
  );

  searchShops$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductSearchActions.searchShops.trigger),
      switchMap((action) =>
        this.productSearchService
          .getShopsByPackagingUnitId({
            ...action.request,
            paymentMethods: (action.request.paymentMethods ??
              '') as Array<PaymentMethodType>,
          })
          .pipe(
            map((response: PageProductShopsPageResponse) =>
              ProductSearchActions.searchShops.success({
                response: response as PageResponse<ProductShopsPageResponse>,
              })
            ),
            catchError(() => of(ProductSearchActions.searchShops.failure()))
          )
      )
    )
  );

  getBrands$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductSearchActions.getBrands.trigger),
      withLatestFrom(
        this.store.select(ProductSearchSelectors.selectSearchValue),
        this.store.select(AuthenticationSelectors.selectUserInfo)
      ),
      switchMap(([_action, { brandList, ...searchValue }, userInfo]) => {
        if (
          userInfo.attributes?.['custom:selectedSiteId'] &&
          userInfo.attributes?.['custom:selectedMerchantType']
        ) {
          return this.productSearchService
            .getBrandsForSearchResult({
              body: { ...searchValue },
              marketplaceSection:
                this.router.url.includes('buying') &&
                !this.router.url.includes('sortiment')
                  ? 'BUYING'
                  : 'SELLING',
            })
            .pipe(
              map((response: Array<string>) =>
                ProductSearchActions.getBrands.success({
                  response: [...response.sort((a, b) => a.localeCompare(b))],
                })
              )
            );
        } else {
          return of(ProductSearchActions.getBrands.success({ response: [] }));
        }
      })
    )
  );

  getActiveProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductSearchActions.getActiveProduct.trigger),
      switchMap((action) =>
        this.productsService
          .getProductSheetById({
            packagingUnitId: action.packagingUnitId,
            marketplaceSection:
              this.router.url.includes('buying') &&
              !this.router.url.includes('sortiment')
                ? 'BUYING'
                : 'SELLING',
          })
          .pipe(
            map((response: ProductSheetResponse) =>
              ProductSearchActions.getActiveProduct.success({
                response,
              })
            )
          )
      )
    )
  );

  getProductVariants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductSearchActions.getProductVariants.trigger),
      switchMap((action) =>
        this.productSearchService
          .getVariantsByPackagingUnitId({
            packagingUnitId: action.packagingUnitId,
          })
          .pipe(
            map((response: Array<ProductVariantResponse>) =>
              ProductSearchActions.getProductVariants.success({ response })
            )
          )
      )
    )
  );

  getCollectorVariants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductSearchActions.getCollectorVariants.trigger),
      switchMap((action) =>
        this.productSearchService
          .getCollectorsByPackagingUnitId({
            packagingUnitId: action.packagingUnitId,
          })
          .pipe(
            map((response: Array<ProductCollectorResponse>) =>
              ProductSearchActions.getCollectorVariants.success({ response })
            )
          )
      )
    )
  );

  getProductSuggestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductSearchActions.getProductSuggestions.trigger),
      switchMap((action) =>
        this.productSearchService
          .getProductNamesAndBrands({
            searchText: action.searchText,
          })
          .pipe(
            map((response: Array<SearchTextExtension>) =>
              ProductSearchActions.getProductSuggestions.success({ response })
            )
          )
      )
    )
  );

  updateSearchValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductSearchActions.updateSearchValue.trigger),
      withLatestFrom(
        this.store.select(ProductSearchSelectors.selectSearchedWords),
        this.store.select(ProductSearchSelectors.selectProductSuggestions)
      ),
      switchMap(([action, searchedWords, productSuggestions]) => {
        if (
          action.filter?.searchText &&
          productSuggestions.some(
            (suggestion) =>
              suggestion.extendedText === action.filter?.searchText
          )
        ) {
          localStorage.setItem('searchedWords', JSON.stringify(searchedWords));
        }
        return of(ProductSearchActions.updateSearchValue.success());
      })
    )
  );

  getSearchedWords$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductSearchActions.getSearchedWords.trigger),
      switchMap((_action) => {
        const searchedWords = localStorage.getItem('searchedWords');
        return of(
          ProductSearchActions.getSearchedWords.success({
            response: searchedWords ? JSON.parse(searchedWords) : {},
          })
        );
      })
    )
  );

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly productSearchService: ProductSearchService,
    private readonly productsService: ProductsService,
    private readonly router: Router
  ) {}
}
